import { ChevronRight, Loader2, Mail, MessageSquare } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { FacebookIcon, InstagramIcon, LinkedInIcon } from '../PlatformIcon';
import {
  useControlCentreMarketingReports,
  type ChannelFigures,
  type SocialFigures,
  type SocialPlatform,
} from '../../hooks/useControlCentreMarketingReports';
import { pctChange } from '../../hooks/useGbpPerformance';

const PLATFORM_LABEL: Record<SocialPlatform, string> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  linkedin: 'LinkedIn',
};

function PlatformIcon({ platform, className }: { platform: SocialPlatform; className?: string }) {
  if (platform === 'facebook') return <FacebookIcon className={className} />;
  if (platform === 'instagram') return <InstagramIcon className={className} />;
  return <LinkedInIcon className={className} />;
}

function fmtCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (n >= 10_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
  return n.toLocaleString();
}

function fmtPct(n: number): string {
  return `${Math.round(n * 10) / 10}%`;
}

function Delta({ value, invert = false }: { value: number | null; invert?: boolean }) {
  if (value == null) return null;
  const rounded = Math.round(Math.abs(value));
  if (rounded === 0) return null;
  const up = value > 0;
  const good = invert ? !up : up;
  return (
    <span className={`shrink-0 text-[10px] font-semibold ${good ? 'text-green-600' : 'text-red-500'}`}>
      {up ? '▲' : '▼'}
      {rounded}%
    </span>
  );
}

function Stat({
  label,
  value,
  delta,
  invert = false,
  note,
}: {
  label: string;
  value: string;
  delta?: number | null;
  invert?: boolean;
  note?: string;
}) {
  return (
    <div className="min-w-0 rounded-xl border border-gray-200 bg-white px-2.5 py-2 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
      <div className="flex min-w-0 items-baseline gap-1">
        <span className="truncate text-[15px] font-bold tabular-nums leading-tight text-gray-900">{value}</span>
        {delta !== undefined ? <Delta value={delta} invert={invert} /> : null}
        {note ? <span className="shrink-0 text-[10px] font-semibold text-gray-400">{note}</span> : null}
      </div>
      <p className="mt-0.5 truncate text-[10px] text-gray-500">{label}</p>
    </div>
  );
}

function CardHeader({
  icon,
  title,
  subtitle,
  onOpen,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  onOpen?: () => void;
}) {
  return (
    <div className="mb-3 flex items-center justify-between gap-3">
      <div className="flex min-w-0 items-center gap-2.5">
        {icon}
        <div className="min-w-0">
          <p className="truncate text-[14px] font-semibold text-gray-900">{title}</p>
          <p className="truncate text-[11px] text-gray-500">{subtitle}</p>
        </div>
      </div>
      {onOpen ? (
        <button
          type="button"
          onClick={onOpen}
          className="inline-flex shrink-0 items-center gap-0.5 text-[12px] font-semibold text-[#1a73e8] hover:underline"
        >
          Open
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      ) : null}
    </div>
  );
}

function IconTile({ children, tone }: { children: React.ReactNode; tone: string }) {
  return (
    <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${tone}`}>{children}</span>
  );
}

function EmailCard({
  current,
  previous,
  onOpen,
}: {
  current: ChannelFigures;
  previous: ChannelFigures;
  onOpen: () => void;
}) {
  return (
    <div data-cc-inset className="p-3 sm:p-4">
      <CardHeader
        icon={
          <IconTile tone="bg-emerald-50 text-emerald-600">
            <Mail className="h-4 w-4" strokeWidth={2.25} />
          </IconTile>
        }
        title="Email blasts"
        subtitle="This month · vs the same days last month"
        onOpen={onOpen}
      />
      {current.campaigns === 0 ? (
        <p className="text-[12px] text-gray-500">
          No email blasts sent this month yet
          {previous.campaigns > 0 ? ` · ${previous.campaigns} by this point last month` : ''}.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4 sm:gap-2">
            <Stat label="Blasts" value={fmtCount(current.campaigns)} delta={pctChange(current.campaigns, previous.campaigns)} />
            <Stat label="Emails sent" value={fmtCount(current.sent)} delta={pctChange(current.sent, previous.sent)} />
            <Stat
              label="Open rate"
              value={current.delivered > 0 ? fmtPct(current.openRate) : '—'}
              delta={current.delivered > 0 && previous.delivered > 0 ? pctChange(current.openRate, previous.openRate) : null}
            />
            <Stat
              label="Click rate"
              value={current.delivered > 0 ? fmtPct(current.clickRate) : '—'}
              delta={current.delivered > 0 && previous.delivered > 0 ? pctChange(current.clickRate, previous.clickRate) : null}
            />
          </div>
          <p className="mt-2 text-[11px] text-gray-500">
            {fmtCount(current.delivered)} delivered · {fmtCount(current.opened)} opened · {fmtCount(current.clicked)} clicked
            {current.unsubscribed > 0 ? ` · ${fmtCount(current.unsubscribed)} unsubscribed` : ''}
          </p>
        </>
      )}
    </div>
  );
}

function SmsCard({
  current,
  previous,
  onOpen,
}: {
  current: ChannelFigures;
  previous: ChannelFigures;
  onOpen: () => void;
}) {
  return (
    <div data-cc-inset className="p-3 sm:p-4">
      <CardHeader
        icon={
          <IconTile tone="bg-sky-50 text-sky-600">
            <MessageSquare className="h-4 w-4" strokeWidth={2.25} />
          </IconTile>
        }
        title="Text blasts"
        subtitle="This month · vs the same days last month"
        onOpen={onOpen}
      />
      {current.campaigns === 0 ? (
        <p className="text-[12px] text-gray-500">
          No text blasts sent this month yet
          {previous.campaigns > 0 ? ` · ${previous.campaigns} by this point last month` : ''}.
        </p>
      ) : (
        <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4 sm:gap-2">
          <Stat label="Blasts" value={fmtCount(current.campaigns)} delta={pctChange(current.campaigns, previous.campaigns)} />
          <Stat label="Texts sent" value={fmtCount(current.sent)} delta={pctChange(current.sent, previous.sent)} />
          <Stat
            label="Texts per blast"
            value={fmtCount(Math.round(current.sent / current.campaigns))}
            delta={previous.campaigns > 0 ? pctChange(current.sent / current.campaigns, previous.sent / previous.campaigns) : null}
          />
          <Stat
            label="Opt-outs"
            value={fmtCount(current.unsubscribed)}
            delta={pctChange(current.unsubscribed, previous.unsubscribed)}
            invert
          />
        </div>
      )}
    </div>
  );
}

function SocialCard({ current, previous }: { current: SocialFigures; previous: SocialFigures }) {
  const label = PLATFORM_LABEL[current.platform];
  const followerNote =
    current.newFollowers == null
      ? undefined
      : current.newFollowers === 0
        ? undefined
        : `${current.newFollowers > 0 ? '+' : ''}${fmtCount(current.newFollowers)}`;
  return (
    <div data-cc-inset className="p-3 sm:p-4">
      <CardHeader
        icon={<PlatformIcon platform={current.platform} className="h-8 w-8 shrink-0" />}
        title={label}
        subtitle={`${current.account ? `${current.account} · ` : ''}This month`}
      />
      <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4 sm:gap-2">
        <Stat label="Posts" value={fmtCount(current.posts)} delta={pctChange(current.posts, previous.posts)} />
        <Stat label="Engagement" value={fmtCount(current.engagement)} delta={pctChange(current.engagement, previous.engagement)} />
        <Stat
          label="Account reach"
          value={current.accountReach > 0 ? fmtCount(current.accountReach) : '—'}
          delta={current.accountReach > 0 ? pctChange(current.accountReach, previous.accountReach) : null}
        />
        <Stat
          label="Followers"
          value={current.followers > 0 ? fmtCount(current.followers) : '—'}
          note={followerNote}
        />
      </div>
      {current.posts > 0 ? (
        <p className="mt-2 text-[11px] text-gray-500">
          {fmtCount(current.likes)} likes · {fmtCount(current.comments)} comments
          {current.reach > 0 ? ` · ${fmtCount(current.reach)} people reached by posts` : ''}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Control Centre marketing performance: email and text blasts from the
 * Marketing app, and one card per connected social account. Which cards
 * appear follows what the business actually uses.
 */
export default function ControlCentreMarketingPanel({ enabled }: { enabled: boolean }) {
  const navigate = useNavigate();
  const reports = useControlCentreMarketingReports(enabled);

  if (!enabled) return null;
  const hasCards = reports.email || reports.sms || reports.social.length > 0;
  if (!reports.loading && !hasCards) return null;

  return (
    <section className="mb-5" data-control-centre-panel aria-label="Marketing performance">
      <header className="flex items-center justify-between px-4 py-3 sm:px-5">
        <h2 className="text-[15px] font-semibold text-gray-900">Marketing</h2>
        <button
          type="button"
          onClick={() => navigate('/marketing')}
          className="inline-flex items-center gap-0.5 text-[12px] font-semibold text-[#1a73e8] hover:underline"
        >
          Open Marketing
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </header>

      <div className="control-centre-panel__body space-y-3">
        {reports.loading && !hasCards ? (
          <div data-cc-inset className="flex items-center gap-2 p-4 text-[13px] text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading marketing…
          </div>
        ) : null}

        {reports.email ? (
          <EmailCard
            current={reports.email.current}
            previous={reports.email.previous}
            onOpen={() => navigate('/marketing?page=email-campaigns')}
          />
        ) : null}

        {reports.sms ? (
          <SmsCard
            current={reports.sms.current}
            previous={reports.sms.previous}
            onOpen={() => navigate('/marketing?page=email-campaigns')}
          />
        ) : null}

        {reports.social.map((entry) => (
          <SocialCard key={entry.current.platform} current={entry.current} previous={entry.previous} />
        ))}

        {reports.error && !hasCards ? (
          <p className="px-1 text-[12px] text-gray-500">Couldn’t load marketing reports.</p>
        ) : null}
      </div>
    </section>
  );
}
